// src/components/layout/ChatColumn/SystemMessage.jsx
import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { parseFirestoreTimestamp } from '@/utils/timeUtils';

/* ========================
 * SystemMessage - Component for displaying system messages and notifications
 * Handles error states, bot switching, and system alerts
 * ======================== */
const SystemMessage = ({
    message, // This should be the rawContent of the system message
    timestamp,
    hasError = false,
    variant = 'default',
    fontSize = 'md',
    type = 'INFO',
    metadata = {}
}) => {
    const parsedTimestamp = useMemo(() => {
        return parseFirestoreTimestamp(timestamp);
    }, [timestamp]);
    
    const messageStyles = useMemo(() => {
        const baseStyles = 'max-w-full px-4 py-2 rounded-lg shadow-sm text-center';
        switch(variant) {
            case 'error': return `${baseStyles} bg-red-600/20 text-red-100 border border-red-500/40`;
            case 'warning': return `${baseStyles} bg-yellow-600/20 text-yellow-100 border border-yellow-500/40`;
            case 'success': return `${baseStyles} bg-green-600/20 text-green-100 border border-green-500/40`;
            case 'default': default: return `${baseStyles} bg-surface-dark/70 text-on-surface-dim border border-white/10`;
        }
    }, [variant]);
    
    if (type === 'bot-switch' && metadata?.previousBot && metadata?.newBot) {
        return (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="w-full flex flex-col items-center justify-center gap-1 my-4">
                <div className="w-full max-w-[400px] bg-surface-dark/70 rounded-xl p-4 border border-white/10 shadow-sm">
                    <div className="text-center text-sm font-medium text-on-surface mb-3">Switched Assistants</div>
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex flex-col items-center">
                            <div className="h-10 w-10 rounded-full bg-primary-dark/30 flex items-center justify-center mb-2"><span className="text-sm font-medium text-primary-light">{metadata.previousBot.botName?.charAt(0) || '?'}</span></div>
                            <div className="text-xs text-on-surface-dim">{metadata.previousBot.botName || 'Unknown'}</div>
                        </div>
                        <div className="flex-1 h-[2px] bg-surface/50 relative">
                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-surface-dark border border-white/10 flex items-center justify-center">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                        </div>
                        <div className="flex flex-col items-center">
                            <div className="h-10 w-10 rounded-full bg-primary/30 flex items-center justify-center mb-2"><span className="text-sm font-medium text-primary-light">{metadata.newBot.botName?.charAt(0) || '?'}</span></div>
                            <div className="text-xs text-on-surface-dim">{metadata.newBot.botName || 'Unknown'}</div>
                        </div>
                    </div>
                    <div className="text-center text-xs text-on-surface-dim mt-3">{parsedTimestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
            </motion.div>
        );
    }
    
    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="w-full flex justify-center">
            <div className={messageStyles}>
                <div className={`text-${fontSize} font-medium`}>{message}</div>
                <div className="text-xs mt-1 opacity-70">{parsedTimestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
            </div>
        </motion.div>
    );
};

export default SystemMessage;